import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/shared/components/ui/button";
import type { AdminTranslation } from "../shared/communications-types";
import { resolveCommunicationError } from "../shared/resolve-communication-error";
import { useActivateVersion } from "./use-translations";

interface VersionListProps {
  /** Versions for one `(channel, locale)`, newest first. */
  readonly versions: readonly AdminTranslation[];
}

export function VersionList({ versions }: VersionListProps) {
  const { t } = useTranslation();
  const activate = useActivateVersion();
  const [error, setError] = useState<string | null>(null);

  const handleActivate = async (translationId: string) => {
    if (activate.isPending) return;
    setError(null);
    try {
      await activate.mutateAsync(translationId);
    } catch (err) {
      setError(resolveCommunicationError(err, t));
    }
  };

  if (versions.length === 0) return null;

  return (
    <section
      data-testid="communication-version-list"
      className="space-y-3 rounded-md border border-border bg-card p-4"
    >
      <h4 className="text-sm font-semibold">{t("communications.versions.heading")}</h4>

      {error && (
        <p data-testid="communication-version-error" className="text-sm text-destructive">
          {error}
        </p>
      )}

      <ul className="space-y-2">
        {versions.map((version) => (
          <li
            key={version.id}
            data-testid={`communication-version-row-${version.version}`}
            className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-background p-3"
          >
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">
                {t("communications.translations.versionLabel", {
                  version: version.version,
                })}
              </span>
              {version.isActive && (
                <span
                  data-testid={`communication-version-active-${version.version}`}
                  className="inline-flex items-center rounded-full bg-wander-orange/15 px-2 py-0.5 text-xs font-medium text-wander-orange ring-1 ring-inset ring-wander-orange/40"
                >
                  {t("communications.versions.active")}
                </span>
              )}
            </div>
            {!version.isActive && (
              <Button
                size="sm"
                variant="outline"
                data-testid={`communication-activate-version-${version.version}`}
                onClick={() => handleActivate(version.id)}
                disabled={activate.isPending}
              >
                {t("communications.versions.activate")}
              </Button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
